import React from "react";
import convert from 'color-convert';
import { useFormInstanceSelect } from "./FormInstance.service";
const defaultStyles = {
    primaryColor: "#f97316",
    backgroundColor: "#ffffff",
    fontFamily: "Inter",
    fontColor: "#374151",
    borderRadius: "medium",
    buttonStyle: "filled",
    buttonSize: "medium",
    chatUserBubbleColor: "#dbeafe",
    chatUserBubbleFontColor: "#1f2937",
    chatAssistantBubbleColor: "#fed7aa",
    chatAssistantBubbleFontColor: "#1f2937"
};
// Convert borderRadius to actual CSS values
const getBorderRadiusValue = (radius) => {
    switch (radius) {
        case "none": return "0px";
        case "small": return "4px";
        case "medium": return "8px";
        case "large": return "12px";
        case "full": return "9999px";
        default: return "8px";
    }
};
// Convert buttonSize to actual CSS values
const getButtonSizeStyles = (size) => {
    switch (size) {
        case "small": return "padding: 0.5rem 1rem; font-size: 0.875rem;";
        case "medium": return "padding: 0.75rem 1.5rem; font-size: 1rem;";
        case "large": return "padding: 1rem 2rem; font-size: 1.125rem;";
        default: return "padding: 0.75rem 1.5rem; font-size: 1rem;";
    }
};
// Convert buttonStyle to actual CSS values
const getButtonStyles = (style, color) => {
    switch (style) {
        case "filled":
            return `
          background-color: ${color};
          color: white;
          border: 2px solid ${color};
        `;
        case "outline":
            return `
          background-color: transparent;
          color: ${color};
          border: 2px solid ${color};
        `;
        case "ghost":
            return `
          background-color: transparent;
          color: ${color};
          border: 2px solid transparent;
        `;
        default:
            return `
          background-color: ${color};
          color: white;
          border: 2px solid ${color};
        `;
    }
};
function perceivedLuminance(hex) {
    const rgb = convert.hex.rgb(hex);
    return ((0.299 * Math.pow(rgb[0], 2)) + (0.587 * Math.pow(rgb[1], 2)) + (0.0722 * Math.pow(rgb[2], 2))) / 255 / 255;
}
function generateDerivedStyles(styles) {
    const backgroundColorHsl = convert.hex.hsl(styles.backgroundColor);
    const backgroundColorPerceivedLuminance = perceivedLuminance(styles.backgroundColor);
    const lightBackgroundColor = backgroundColorPerceivedLuminance > 0.4;
    const derivedStyles = { ...styles };
    derivedStyles.backgroundColorHsl = backgroundColorHsl;
    if (lightBackgroundColor) {
        derivedStyles.backgroundColorLightHex = '#' + convert.hsl.hex([backgroundColorHsl[0] + 10, backgroundColorHsl[1], Math.min(100, backgroundColorHsl[2] + 10)]);
        derivedStyles.backgroundColorOpaqueHex = '#' + convert.hsl.hex([Math.max(0, backgroundColorHsl[0] - 10), backgroundColorHsl[1], Math.max(0, backgroundColorHsl[2] - 5)]);
        derivedStyles.backgroundColorDarkHex = '#' + convert.hsl.hex([Math.max(0, backgroundColorHsl[0] - 10), backgroundColorHsl[1], Math.max(0, backgroundColorHsl[2] - 15)]);
        derivedStyles.backgroundColorGradientStartHex = '#' + convert.hsl.hex([backgroundColorHsl[0], backgroundColorHsl[1], Math.max(0, backgroundColorHsl[2] - 2)]);
    }
    else {
        derivedStyles.backgroundColorLightHex = '#' + convert.hsl.hex([backgroundColorHsl[0] - 10, backgroundColorHsl[1], Math.min(100, backgroundColorHsl[2] - 10)]);
        derivedStyles.backgroundColorOpaqueHex = '#' + convert.hsl.hex([Math.max(0, backgroundColorHsl[0] - 10), backgroundColorHsl[1], Math.max(0, backgroundColorHsl[2] + 5)]);
        derivedStyles.backgroundColorDarkHex = '#' + convert.hsl.hex([Math.max(0, backgroundColorHsl[0] + 10), backgroundColorHsl[1], Math.max(0, backgroundColorHsl[2] + 15)]);
        derivedStyles.backgroundColorGradientStartHex = '#' + convert.hsl.hex([backgroundColorHsl[0], backgroundColorHsl[1], Math.max(0, backgroundColorHsl[2] - 2)]);
    }
    const fontColorHsl = convert.hex.hsl(styles.fontColor);
    derivedStyles.fontColorHsl = fontColorHsl;
    derivedStyles.fontColorLightHex = '#' + convert.hsl.hex([fontColorHsl[0], fontColorHsl[1], Math.min(100, fontColorHsl[2] + 10)]);
    derivedStyles.fontColorDarkHex = '#' + convert.hsl.hex([fontColorHsl[0], fontColorHsl[1], Math.max(0, fontColorHsl[2] - 10)]);
    const baseLightness = 35;
    const lightnessAdjust = 30 * (1 - backgroundColorPerceivedLuminance);
    const validColorHslArray = [100, 50, baseLightness];
    derivedStyles.validColorHex = '#' + convert.hsl.hex([validColorHslArray[0], validColorHslArray[1], validColorHslArray[2] + lightnessAdjust]);
    const warningColorHslArray = [30, 80, baseLightness];
    derivedStyles.warningColorHex = '#' + convert.hsl.hex([warningColorHslArray[0], warningColorHslArray[1], warningColorHslArray[2] + lightnessAdjust]);
    const invalidColorHslArray = [0, 80, baseLightness];
    derivedStyles.invalidColorHex = '#' + convert.hsl.hex([invalidColorHslArray[0], invalidColorHslArray[1], invalidColorHslArray[2] + lightnessAdjust]);
    // Calculate chat bubble derived styles
    if (styles.chatUserBubbleColor) {
        const userBubbleHsl = convert.hex.hsl(styles.chatUserBubbleColor);
        // Darker border: decrease lightness by 15
        derivedStyles.chatUserBubbleBorderHex = '#' + convert.hsl.hex([
            userBubbleHsl[0],
            Math.min(100, userBubbleHsl[1] + 10),
            Math.max(0, userBubbleHsl[2] - 15)
        ]);
        // Gradient end: slightly lighter
        derivedStyles.chatUserBubbleGradientEndHex = '#' + convert.hsl.hex([
            userBubbleHsl[0],
            userBubbleHsl[1],
            Math.min(100, userBubbleHsl[2] + 10)
        ]);
    }
    if (styles.chatAssistantBubbleColor) {
        const assistantBubbleHsl = convert.hex.hsl(styles.chatAssistantBubbleColor);
        // Darker border: decrease lightness by 15
        derivedStyles.chatAssistantBubbleBorderHex = '#' + convert.hsl.hex([
            assistantBubbleHsl[0],
            Math.min(100, assistantBubbleHsl[1] + 10),
            Math.max(0, assistantBubbleHsl[2] - 15)
        ]);
        // Gradient end: slightly lighter
        derivedStyles.chatAssistantBubbleGradientEndHex = '#' + convert.hsl.hex([
            assistantBubbleHsl[0],
            assistantBubbleHsl[1],
            Math.min(100, assistantBubbleHsl[2] + 10)
        ]);
    }
    return derivedStyles;
}
export function cssContent(cssId, mergedStyles, isPrimary = true, headless = false) {
    const buttonColor = mergedStyles.primaryColor;
    return `
    /* Fluid Form Dynamic Styles */
    #${cssId} {
      font-family: ${mergedStyles.fontFamily}, system-ui, -apple-system, sans-serif;
      color: ${mergedStyles.fontColor};
    }

    #${cssId} .fluid-form-title {
      font-size: 1.5rem;
      font-weight: 600;
      color: ${mergedStyles.primaryColor};
      margin-bottom: 1.5rem;
      text-align: left;
    }
    
    /* Background Colors */
    #${cssId} .f-bg {
      background-color: hsl(${mergedStyles.backgroundColorHsl[0]}, ${mergedStyles.backgroundColorHsl[1]}%, ${mergedStyles.backgroundColorHsl[2]}%);
    }
    
    #${cssId} .f-bg-light {
      background-color: ${mergedStyles.backgroundColorLightHex};
    }
    
    #${cssId} .f-bg-opaque {
      background-color: ${mergedStyles.backgroundColorOpaqueHex};
    }
    
    #${cssId} .hover\\:f-bg-opaque:hover {
      --tw-bg-opacity: 1;
      background-color: ${mergedStyles.backgroundColorOpaqueHex};
    }
    
    #${cssId} .f-bg-dark {
      background-color: ${mergedStyles.backgroundColorDarkHex}; 
    }
    
    #${cssId} .f-bg-gradient {
      background-image: linear-gradient(to right, ${mergedStyles.backgroundColorGradientStartHex}, ${mergedStyles.backgroundColor});
    }
    
    /* Border Colors */
    #${cssId} .f-border {
      border: 1px solid ${mergedStyles.backgroundColorDarkHex};
    }
    
    #${cssId} .f-border-b {
      border-bottom: 1px solid ${mergedStyles.backgroundColorDarkHex};
    }
    
    #${cssId} .f-border-t {
      border-top: 1px solid ${mergedStyles.backgroundColorDarkHex};
    }
    
    #${cssId} .f-border-l {
      border-left: 1px solid ${mergedStyles.backgroundColorDarkHex};
    }
    
    #${cssId} .f-border-r {
      border-right: 1px solid ${mergedStyles.backgroundColorDarkHex};
    }
    
    /* Font Colors */
    #${cssId} .f-text {
      color: hsl(${mergedStyles.fontColorHsl[0]}, ${mergedStyles.fontColorHsl[1]}%, ${mergedStyles.fontColorHsl[2]}%);
    }
    
    #${cssId} .f-text-light {
      color: ${mergedStyles.fontColorLightHex};
    }
    
    #${cssId} .f-text-dark {
      color: ${mergedStyles.fontColorDarkHex};
    }
    
    /* Primary Colors */
    #${cssId} .fluid-primary-color {
      color: ${mergedStyles.primaryColor};
    }
    
    #${cssId} .fluid-primary-bg {
      background-color: ${mergedStyles.primaryColor};
    }
    
    /* Border Radius */
    #${cssId} .f-border-radius {
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
    }
    
    /* Form Inputs */
    #${cssId} .fluid-input {
      padding: 0.7rem 0.625rem;
      border: 1px solid ${mergedStyles.backgroundColorDarkHex};
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      color: #374151;
      font-size: 0.875rem;
      line-height: 1.25rem;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
      transition: all 0.15s ease-in-out;
      background: white;
      font-family: inherit;
    }
    
    #${cssId} .fluid-input.fluid-input-invalid {
      border-color: hsl(3, 100%, 90%);
      background-color: hsl(3, 100%, 98%);
    }
    
    #${cssId} .fluid-input.fluid-input-valid {
      border-color: hsl(120, 39%, 82%);
      background-color: hsl(120, 39%, 98%);
    }
    
    #${cssId} .fluid-input.fluid-input-warning {
      border-color: hsl(45, 100%, 80%);
      background-color: hsl(45, 100%, 98%);
    }

    #${cssId} .f-color-valid {
      color: ${mergedStyles.validColorHex};
    }
    
    #${cssId} .f-bg-valid {
      background-color: ${mergedStyles.validColorHex};
    }

    #${cssId} .f-color-warning {
      color: ${mergedStyles.warningColorHex};
    }
    
    #${cssId} .f-bg-warning {
      background-color: ${mergedStyles.warningColorHex};
    }

    #${cssId} .f-color-invalid {
      color: ${mergedStyles.invalidColorHex};
    }
    
    #${cssId} .f-bg-invalid {
      background-color: ${mergedStyles.invalidColorHex};
    }

    #${cssId} .fluid-textarea {
      width: 100%;
    }
    
    #${cssId} .fluid-input:focus, #${cssId} .fluid-input-focus {
      border-color: transparent;
      box-shadow: 0 0 0 2px ${mergedStyles.primaryColor}40;
      outline: none;
    }
    
    #${cssId} .fluid-input::placeholder {
      color: #9ca3af;
    }
    
    /* Buttons */
    #${cssId} .fluid-button-primary {
      ${getButtonStyles(mergedStyles.buttonStyle, buttonColor)}
      ${getButtonSizeStyles(mergedStyles.buttonSize)}
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      font-family: inherit;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease-in-out;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    
    #${cssId} .fluid-button-primary:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
    
    #${cssId} .fluid-button-secondary {
      ${getButtonStyles(mergedStyles.buttonStyle, buttonColor)}
      ${getButtonSizeStyles(mergedStyles.buttonSize)}
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      font-family: inherit;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease-in-out;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    
    #${cssId} .fluid-button-secondary:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
    
    /* Labels */
    #${cssId} .fluid-label {
      font-family: inherit;
      font-weight: 600;
      color: ${mergedStyles.fontColor};
      margin-bottom: 0.5rem;
      display: block;
    }
    
    /* Dropdowns */
    #${cssId} .fluid-dropdown {
      padding: 0.7rem 0.625rem;
      transition: none;
    }

    #${cssId} .fluid-dropdown.multiple {
      padding: 0.45rem 0.625rem;
    }

    #${cssId} .fluid-dropdown.multiple .fluid-dropdown-label {
      padding: 0.45rem 0.625rem;
    }
    
    #${cssId} .fluid-dropdown:focus,
    #${cssId} .fluid-dropdown.active {
      border-color: transparent;
      box-shadow: 0 0 0 2px ${mergedStyles.primaryColor}40;
    }
    
    #${cssId} .ui.dropdown {
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)} !important;
      font-family: inherit !important;
    }
    
    #${cssId} div.ui.fluid-dropdown.active {
      border-color: transparent !important;
      border-bottom-left-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      border-top-left-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)} !important;
      border-top-right-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)} !important;
    }
    
    #${cssId} div.ui.fluid-dropdown.active.upward {
      border-top-left-radius: 0 !important;
      border-top-right-radius: 0 !important;
      border-bottom-left-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)} !important;
      border-bottom-right-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)} !important;
    }
    #${cssId} .fluid-dropdown .menu {
      border-color: transparent;
      border: 2px solid ${mergedStyles.primaryColor}40;
      border-top: none;
      border-bottom-left-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      border-bottom-right-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      min-width: calc(100% + 5px);
      width: calc(100% + 5px);
      left: -2px;
    }
    
    #${cssId} .fluid-dropdown.upward .menu {
      border-top-left-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      border-top-right-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-top: 2px solid ${mergedStyles.primaryColor}40 !important;
      border-bottom: none;
    }
    
    /* Tables */
    #${cssId} .fluid-table {
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
    }
    
    #${cssId} .fluid-table-header {
      background-color: ${mergedStyles.primaryColor};
      color: white;
      font-family: inherit;
      font-weight: 600;
    }
    
    #${cssId} .fluid-table-cell {
      font-family: inherit;
      border-color: #e5e7eb;
    }
    
    /* Section Titles */
    #${cssId} .fluid-section-title {
      font-family: inherit;
      font-weight: 700;
      margin-bottom: 1rem;
      color: ${mergedStyles.primaryColor};
      text-align: left;
    }

    #${cssId} .fluid-section-title.level-1 {
      color: ${mergedStyles.primaryColor};
      font-size: 1.5rem;
    }

    #${cssId} .fluid-section-title.level-2 {
      font-size: 1.2rem;
    }

    #${cssId} .fluid-section-title.level-3 {
      font-size: 1.1rem;
    }

    #${cssId} .fluid-section-title.level-4 {
      font-size: 1rem;
    }
    
    #${cssId} .fluid-section-title.level-5 {
      font-size: 0.9rem;
    }
    
    /* Cards */
    #${cssId} .fluid-card {
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      font-family: inherit;
    }
    
    /* Form Fields Container */
    #${cssId} .fluid-form-fields {
      font-family: inherit;
    }
    
    /* Composed Field Styling */
    #${cssId} .fluid-composed-field {
      margin-bottom: 1.5rem;
    }
    
    #${cssId} .fluid-composed-field .fluid-section-title {
      border-bottom: 2px solid ${mergedStyles.primaryColor};
      padding-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 2px;
    }
    
    /* Action Icons */
    #${cssId} .fluid-action-icon {
      color: ${mergedStyles.primaryColor};
      cursor: pointer;
      transition: all 0.15s ease-in-out;
    }
    
    #${cssId} .fluid-action-icon:hover {
      transform: scale(1.1);
    }
    
    /* Delete Icon Specific */
    #${cssId} .fluid-delete-icon {
      color: #ef4444;
    }
    
    #${cssId} .fluid-delete-icon:hover {
      color: #dc2626;
    }
    
    /* Success/Error Messages */
    #${cssId} .fluid-success-message {
      background-color: #dcfce7;
      border: 1px solid #bbf7d0;
      color: #166534;
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      padding: 1rem;
      font-family: inherit;
    }
    
    #${cssId} .fluid-error-message {
      background-color: #fef2f2;
      border: 1px solid #fecaca;
      color: #dc2626;
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      padding: 1rem;
      font-family: inherit;
    }
    
    /* Form Input Modes */
    #${cssId} .fluid-input-modes {
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
    }
    

    
    #${cssId} .fluid-input-mode-button {
      color: white;
      
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)};
      padding: 0.5rem;
      cursor: pointer;
      transition: all 0.15s ease-in-out;
    }
    
    #${cssId} .fluid-input-mode-button:hover {
      background: ${mergedStyles.primaryColor};
      border-color: ${mergedStyles.primaryColor};
    } 
    
    /* Override Semantic UI styles with our theme */
    #${cssId} .fluid-button-primary {
      ${getButtonStyles(mergedStyles.buttonStyle, buttonColor)}
      ${getButtonSizeStyles(mergedStyles.buttonSize)}
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)} !important;
      font-family: inherit !important;
    }
    
    #${cssId} .fluid-button-secondary {
      ${getButtonStyles(mergedStyles.buttonStyle, buttonColor)}
      ${getButtonSizeStyles(mergedStyles.buttonSize)}
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)} !important;
      font-family: inherit !important;
    }
    
    #${cssId} .ui.input > input {
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)} !important;
      font-family: inherit !important;
    }
    
    #${cssId} .ui.input > input:focus {
      border-color: ${mergedStyles.primaryColor} !important;
      box-shadow: 0 0 0 2px ${mergedStyles.primaryColor}40 !important;
    }
    
    #${cssId} .ui.checkbox label {
      font-family: inherit !important;
      color: inherit !important;
    }
    
    
    #${cssId} .ui.table {
      border-radius: ${getBorderRadiusValue(mergedStyles.borderRadius)} !important;
      font-family: inherit !important;
    }
    
    #${cssId} .ui.table thead th {
      background-color: ${mergedStyles.primaryColor} !important;
      color: white !important;
      font-family: inherit !important;
    }

    #${cssId} .fluid-primary-border-left {
      border-left: 2px solid ${mergedStyles.primaryColor} !important;
    }

    #${cssId} .fluid-form-container {
      ${props => props.headless ? 'height: 100%;' : ''}
    }

  `;
}
export const useStyleConfig = () => {
    const configService = useFormInstanceSelect((state) => state.fluidForm);
    const globalStyles = configService?.getAdditionalConfigValue("styleConfig", "globalStyles")?.[0] || defaultStyles;
    const formStyles = configService?.getAdditionalConfigValue("styleConfig", "formStyles")?.[0] || defaultStyles;
    const mergedStyles = { ...defaultStyles, ...globalStyles, ...formStyles };
    return generateDerivedStyles(mergedStyles);
};
export const EmbeddedStyles = ({ styles = {}, style = {}, headless, children }) => {
    const configService = useFormInstanceSelect((state) => state.fluidForm);
    const smallRandomId = Math.random().toString(36).substring(2, 15);
    const cssId = `form-container-${smallRandomId}`;
    const globalStyles = configService?.getAdditionalConfigValue("styleConfig", "globalStyles")?.[0] || defaultStyles;
    const formStyles = configService?.getAdditionalConfigValue("styleConfig", "formStyles")?.[0] || defaultStyles;
    const mergedStyles = { ...globalStyles, ...formStyles, ...styles };
    const derivedStyles = generateDerivedStyles(mergedStyles);
    return React.createElement(React.Fragment, null,
        React.createElement("style", { dangerouslySetInnerHTML: { __html: cssContent(cssId, derivedStyles, true, headless) } }),
        React.createElement("div", { className: "fluid-form-container @container/fluid-form-container @fluid-form-container", id: cssId, style: { ...style, ...(headless ? { height: '100%' } : {}) } }, children));
};
